import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import {
  AdminVacationSchedulesListItem,
  VacationsService,
} from '@app/gen/vacations';
import { Entity } from '@app/types';

import { SettingsState } from '../../settings.state';

export const getVacationsCompanies = createAsyncThunk<
  AdminVacationSchedulesListItem[],
  void,
  ThunkExtra
>(
  'settings/vacations/getCompanies',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const vacationsService = inject(VacationsService);

      const result = await vacationsService.getVacationScheduleAdminList();

      return result.companies;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteVacationSchedule = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    scheduleId: string;
    companyName: string;
    year: number;
  }>,
  ThunkExtra
>(
  'settings/vacations/deleteSchedule',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { companyName, scheduleId, year } = values;

    try {
      const vacationsService = inject(VacationsService);

      await vacationsService.deleteVacationSchedule({
        params: { schedule_id: scheduleId },
      });

      dispatch(
        showCustomNotification([
          {
            title: 'Планирование удалено!',
            type: 'info',
            subtitle: companyName,
            message: `График отпусков ${year}`,
          },
        ]),
      );

      actions.resolve(null);

      dispatch(getVacationsCompanies());
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось удалить планирование!',
            subtitle: companyName,
            message: `График отпусков ${year}`,
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsVacationsListReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getVacationsCompanies.pending, (state) => {
    state.vacationsList.status = 'loading';
  });
  builder.addCase(getVacationsCompanies.fulfilled, (state, { payload }) => {
    state.vacationsList.status = 'complete';
    state.vacationsList.companies = payload;
  });
  builder.addCase(
    getVacationsCompanies.rejected,
    (state, { payload, error, meta }) => {
      if (!meta.aborted) {
        state.vacationsList.status = 'failed';
        state.vacationsList.error =
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError);
      }
    },
  );
};
